import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';

import {
  JOURNEY_TILE_RENDER_CAP,
  SOURCE_TIERS,
  dayKeySchema,
  generationReasonSchema,
  outcomeSourceSchema,
  practiceOutcomeSchema,
  sourceTierSchema,
} from './schema';

/**
 * The journey pathway's event payloads — the half of the journey contract that
 * crosses the env bus rather than being stored (the stored shapes both sides
 * touch live in `./schema`). All are thin pointers by design: ids, counts, and
 * enum values only — no practice content, no tile copy, no free text — because
 * the tables are the source of truth and consumers treat delivery as
 * at-least-once.
 *
 * `.strict()` on all, for the same reason the seam's stored blobs are strict:
 * these payloads cross a bus and are re-parsed by consumers, so a silently
 * carried unknown key would read as part of the contract to whoever found it.
 *
 * These describe the payload the producer authors, which is not quite what a
 * consumer receives: for a type with a catalog status, the envelope builder
 * adds `status` to `data` after the producer has validated. A consumer
 * re-parsing `data` sets that one key aside first, or it rejects every event.
 */

/**
 * Published once per successful composer run, only after both table writes
 * commit. `orderedPracticeIds` mirrors the new live day-plan's arrangement;
 * `newPracticeIds` is the subset this run created. `slotsComposed` and
 * `missCount` can be zero — a run that filled nothing still reports itself —
 * but a run is never asked for zero slots.
 */
export const journeyPracticesComposedV1 = z
  .object({
    memberId: memberIdSchema,
    dayKey: dayKeySchema,
    batchId: z.string().uuid(),
    dayPlanId: z.string().uuid(),
    reason: generationReasonSchema,
    orderedPracticeIds: z.array(z.string().uuid()),
    newPracticeIds: z.array(z.string().uuid()),
    slotsRequested: z.number().int().min(1),
    slotsComposed: z.number().int().min(0),
    missCount: z.number().int().min(0),
  })
  .strict();
export type JourneyPracticesComposedV1 = z.infer<typeof journeyPracticesComposedV1>;

/**
 * The command that triggers a composition — consumed from the env bus, never
 * over HTTP. Slot arithmetic is the publisher's job, so the payload carries
 * the already-decided count; the consumer's idempotency anchor is the
 * EventBridge envelope id, not anything in this payload.
 */
export const journeyCompositionRequestedV1 = z
  .object({
    memberId: memberIdSchema,
    dayKey: dayKeySchema,
    // Upper bound is a model-spend guard: each slot is a sequential Bedrock
    // call, so an unbounded request amplifies a buggy publisher into minutes
    // of spend times the bus retries. Slot arithmetic tops out at 3 + retriggers.
    slotsToCreate: z.number().int().min(1).max(10),
    reason: generationReasonSchema,
  })
  .strict();
export type JourneyCompositionRequestedV1 = z.infer<typeof journeyCompositionRequestedV1>;

/**
 * Published once per successful journey practice launch — the session-mint
 * path's durable announcement that a voice session is running a composed
 * practice. The session row carries the same provenance as columns, so
 * downstream done-marking and retriggers join on the ids rather than read
 * content from here. `firstEver` is derived at launch: true when the member
 * has had no prior journey conversation — a prior session where they spoke,
 * not merely one they opened (no flag storage); `reused` mirrors the mint's session-reuse
 * outcome so a re-issued room is distinguishable from a fresh launch.
 * `dayPlanId` is nullable: a practice can be launched from a tile whose
 * day-plan no longer has a live row for that day, and the event still fires —
 * consumers joining on the day-plan treat null as "launch without a live
 * arrangement", never as a missing event.
 */
/**
 * Published once per newly created practice outcome — the durable announcement
 * that a launched practice's conversation completed and cleared the member-turn
 * floor. Publish-on-create only: at-least-once delivery, a relaunch of an
 * already-done tile, and the fast-path/reconciliation race all collapse to the
 * outcomes table's uniqueness, so a consumer never sees two of these for one
 * practice. The trigger layer's on-completion +1 is the named consumer; it
 * computes slot arithmetic itself and joins on the ids — nothing here carries
 * content. `dayPlanId` and `sessionId` are nullable for the same reason the
 * launch event's plan pointer is: the fact is real even when the pointer's
 * referent is not resolvable at decision time.
 */
export const journeyPracticeCompletedV1 = z
  .object({
    memberId: memberIdSchema,
    composedPracticeId: z.string().uuid(),
    dayPlanId: z.string().uuid().nullable(),
    dayKey: dayKeySchema,
    outcome: practiceOutcomeSchema,
    source: outcomeSourceSchema,
    sessionId: z.string().uuid().nullable(),
    sourceTier: sourceTierSchema,
    generationReason: generationReasonSchema,
  })
  .strict();
export type JourneyPracticeCompletedV1 = z.infer<typeof journeyPracticeCompletedV1>;

export const journeyPracticeLaunchedV1 = z
  .object({
    memberId: memberIdSchema,
    sessionId: z.string().uuid(),
    composedPracticeId: z.string().uuid(),
    dayPlanId: z.string().uuid().nullable(),
    // Code-hosted technique-catalogue key from the launched practice's row
    // (its technique_slug column) — bounded by construction, never member
    // free text.
    techniqueSlug: z.string().min(1).max(64),
    sourceTier: sourceTierSchema,
    generationReason: generationReasonSchema,
    firstEver: z.boolean(),
    reused: z.boolean(),
  })
  .strict();
export type JourneyPracticeLaunchedV1 = z.infer<typeof journeyPracticeLaunchedV1>;

/**
 * The tiles a member was actually shown on one home fetch — the presented
 * denominator that launches and completions are measured against. Derived
 * from the built document, not the composer's plan: a plan row counts what
 * was composed, and between that and the screen tiles are capped, dropped,
 * or promoted into the hero band, so only the rendered arrangement is the
 * honest count. `renderedPracticeIds` is in rendered order, hero first when
 * `heroPromoted`, and `tileCount` is its length by refinement rather than by
 * trust. Bounded at the render cap because a document can never show more.
 * Never the tile title or hook: the practice id joins to the composed-practice
 * row for copy, and copy composed for one member has no business on the bus.
 */
/**
 * The tier of a whole arrangement: the tiles' shared tier when every rendered
 * practice came from the same one, `mixed` when they did not. Per arrangement
 * rather than per tile because the event names the arrangement — and because
 * a per-tile list would be one more array to lift, when the first question
 * anyone asks of the denominator is "composed or fallback?". Named for the
 * arrangement rather than reusing the per-practice `sourceTier` key so the
 * forwarder's registry can bound the two independently: the per-practice key
 * never admits `mixed`.
 */
export const ARRANGEMENT_SOURCE_TIERS = [...SOURCE_TIERS, 'mixed'] as const;
export const arrangementSourceTierSchema = z.enum(ARRANGEMENT_SOURCE_TIERS);
export type ArrangementSourceTier = z.infer<typeof arrangementSourceTierSchema>;

export const journeyTilesPresentedV1 = z
  .object({
    memberId: memberIdSchema,
    dayKey: dayKeySchema,
    dayPlanId: z.string().uuid(),
    renderedPracticeIds: z.array(z.string().uuid()).min(1).max(JOURNEY_TILE_RENDER_CAP),
    // Absent, not guessed, when a rendered practice's tier is unknown: the
    // event still counts the arrangement and only the tier dimension is lost.
    arrangementTier: arrangementSourceTierSchema.optional(),
    heroPromoted: z.boolean(),
    tileCount: z.number().int().min(1).max(JOURNEY_TILE_RENDER_CAP),
    dayZero: z.boolean(),
    replacementPending: z.boolean(),
  })
  .strict()
  .refine((payload) => payload.tileCount === payload.renderedPracticeIds.length, {
    message: 'tileCount must equal renderedPracticeIds.length',
    path: ['tileCount'],
  });
export type JourneyTilesPresentedV1 = z.infer<typeof journeyTilesPresentedV1>;
